import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowRight, Eye, EyeOff, KeyRound, LockKeyhole, ShieldCheck, UserRound } from "lucide-react";
import "../styles/Login.css";
import { getApiBaseUrl } from "../utils/baseUrl";
import { getDashboardRoute } from "../utils/dashboardRoute";

const Login = () => {
  const [serviceNumber, setServiceNumber] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [twoFactor, setTwoFactor] = useState(null);
  const [authenticatorCode, setAuthenticatorCode] = useState("");
  const [lostAuthenticator, setLostAuthenticator] = useState(false);
  const [recoveryContact, setRecoveryContact] = useState("");
  const [recoveryChannel, setRecoveryChannel] = useState("EMAIL");
  const navigate = useNavigate();
  const location = useLocation();
  const loginStyle = {
    "--auth-logo": `url(${process.env.PUBLIC_URL}/army-logo.png)`,
  };

  const BASE_URL = getApiBaseUrl();

  const formatPath = (str) =>
    str?.toLowerCase().replace(/\s+/g, "-").replace(/\//g, "--") || "unknown";
  const normalizeMatch = (str) => formatPath(str).toUpperCase();

  const SIGNAL_ROUTE_HINTS = new Set([
    "SIGNAL BRANCH",
    "KAHAWA GARRISON - SIG BN",
    "AHQ SIG BN",
    "BATTALION",
    "SOS",
    "SOS BRANCH",
    "KACS",
    "IT SQDRN",
    "COMMS SQDRN",
  ]);

  const INFANTRY_ROUTE_HINTS = new Set([
    "2 BDE",
    "2 BDE HQ",
    "3 KR",
    "5 KR",
    "9 KR",
    "21 TPT COY",
    "22 FLD WKSP",
    "23 OCC",
    "4 BDE",
    "4 BDE HQ",
    "1 KR",
    "7 KR",
    "15 KR",
    "41 TPT COY",
    "42 FLD WKSP",
    "43 OCC",
    "6 BDE",
    "6 BDE HQ",
    "17 KR",
    "19 KR",
    "21 KR",
    "61 TPT COY",
    "62 FLD WKSP",
    "63 OCC",
    "8 BDE",
    "8 BDE HQ",
    "23 MIB",
    "25 MIB",
    "27 MIB",
    "110 MIB",
    "110 MIB HQ",
    "31 MIB",
    "33 MIB",
    "35 MIB",
    "ARMD",
    "ARTY",
    "COMBAT ENGRS",
    "CONSTR ENGRS",
    "EMB GAR HQS",
    "KAHAWA GARRISON",
    "LANGATA GARRISON",
    "MARIAKANI GARRISON",
    "GILGIL GARRISON / WESTCOM",
    "MEDICAL CORPS",
  ]);

  const ASOB_ROUTE_HINTS = new Set([
    "MAIN HQ",
    "20 PARA BN",
    "60 ASB",
    "40 RANGER",
    "SQTS",
    "CAU",
  ]);

  const extractErrorMessage = (data) => {
    if (!data) return "Login failed";

    // Common patterns:
    // { message: "..." }
    if (typeof data.message === "string" && data.message.trim()) return data.message;

    // { errors: [{ msg: "..." }, ...] } (express-validator)
    if (Array.isArray(data.errors) && data.errors.length) {
      const first = data.errors[0];
      if (typeof first === "string") return first;
      if (first?.msg) return first.msg;
      return "Validation error";
    }

    // Any other object
    if (typeof data === "string") return data;

    return "Login failed";
  };

  useEffect(() => {
    setIsAdminMode(location.pathname === "/admin");
    setTwoFactor(null);
    setAuthenticatorCode("");
    setLostAuthenticator(false);
  }, [location.pathname]);

  const completeUserLogin = (data) => {
    const { token, name, username, rank, role, branch, subBranch, unitBranch, unit } = data;
    localStorage.removeItem("adminToken");
    localStorage.setItem("authToken", token);
    localStorage.setItem("serviceNumber", serviceNumber.trim());
    localStorage.setItem("name", name || "");
    localStorage.setItem("username", username || serviceNumber.trim());
    localStorage.setItem("role", role || "");
    localStorage.setItem("rank", rank || "");
    localStorage.setItem("branch", branch || "");
    localStorage.setItem("subBranch", subBranch || "");
    localStorage.setItem("unitBranch", unitBranch || "");
    localStorage.setItem("unit", unit || "");
    toast.success("Login successful! Redirecting...");
    navigate(getDashboardRoute({ branch, subBranch, role }), { replace: true });
  };

  const handleTwoFactorVerification = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      const response = await fetch(`${BASE_URL}/api/users/login/verify-2fa`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          challengeToken: twoFactor?.challengeToken,
          token: authenticatorCode,
        }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        const msg = extractErrorMessage(data);
        setError(msg);
        toast.error(msg);
        return;
      }
      completeUserLogin(data);
    } catch {
      const msg = "Unable to verify the authenticator code. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const requestAuthenticatorReset = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      const response = await fetch(`${BASE_URL}/api/users/authenticator-reset/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ serviceNumber: serviceNumber.trim(), contact: recoveryContact.trim(), channel: recoveryChannel }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "Request failed.");
      toast.success(data.message);
      setLostAuthenticator(false);
    } catch (err) {
      setError(err.message || "Unable to submit the reset request.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    let timeout = null;

    try {
      const controller = new AbortController();
      timeout = setTimeout(() => controller.abort(), 15000);

      const endpoint = isAdminMode ? "/api/admin/login" : "/api/users/login";
      const payload = isAdminMode
        ? { username: serviceNumber.trim(), password }
        : { serviceNumber: serviceNumber.trim(), password };

      const response = await fetch(`${BASE_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      // Some backends return empty body on error; guard it
      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        // ✅ Log the REAL backend error so we can fix the backend/fields fast
        console.log("❌ Login failed status:", response.status);
        console.log("❌ Login failed response body:", data);

        const msg = extractErrorMessage(data);
        setError(msg);
        toast.error(msg);
        return;
      }

      if (isAdminMode) {
        localStorage.removeItem("authToken");
        sessionStorage.removeItem("authToken");
        localStorage.setItem("adminToken", data.token);
        localStorage.setItem("adminUsername", serviceNumber.trim());
        localStorage.setItem("adminLevel", data.adminLevel === "SUPERADMIN" ? "SUPERADMIN" : "ADMIN");
        localStorage.setItem("adminUnit", data.adminUnit || "");
        toast.success("Admin login successful! Redirecting...");
        navigate("/admin-dashboard");
        return;
      }

      if (data.requiresTwoFactor) {
        setTwoFactor(data);
        setAuthenticatorCode("");
        return;
      }

      // Save user info locally
      const { token, name, username, role, branch, subBranch, unitBranch, unit } = data;

      localStorage.removeItem("adminToken");
      localStorage.setItem("authToken", token);
      localStorage.setItem("serviceNumber", serviceNumber.trim());
      localStorage.setItem("name", name || "");
      localStorage.setItem("username", username || serviceNumber.trim());
      localStorage.setItem("role", role || "");
      localStorage.setItem("branch", branch || "");
      localStorage.setItem("subBranch", subBranch || "");
      localStorage.setItem("unitBranch", unitBranch || "");
      localStorage.setItem("unit", unit || "");

      toast.success("Login successful! Redirecting...");

      // Route from the appointment returned by the authenticated API response.
      // This keeps registration, authorization and dashboard identity aligned
      // and prevents unrelated fallback roles from being granted on login.
      const dashboardRoute = getDashboardRoute({ branch, subBranch, role });
      if (dashboardRoute) {
        navigate(dashboardRoute);
        return;
      }

const branchNorm = normalizeMatch(branch);
const subBranchNorm = normalizeMatch(subBranch);
const roleNorm = normalizeMatch(role);

// ===== KMA (TRAINING INSTITUTIONS) =====
if ((branchNorm === "TRAINING INSTITUTIONS" || branchNorm === "KMA" || subBranchNorm === "TRAINING INSTITUTIONS" || subBranchNorm === "KMA" || unit === "TRAINING INSTITUTIONS") && subBranch) {
  // All KMA roles use the generic dynamic router
  navigate(`/dashboard/${formatPath(branch)}/${formatPath(subBranch)}/${formatPath(role)}`);
  return;
}

// ===== SOI =====
if (unit === "SOI" || branchNorm === "SOI" || subBranchNorm === "SOI") {
  // Roles that share Master Clerk dashboard (with role-specific routes)
  const masterClerkRoutes = {
    Clerk: "/dashboard/soi/n-a/clerk",
    "Chief Clerk": "/dashboard/soi/n-a/chief-clerk",
    "Dorcus Clerk": "/dashboard/soi/n-a/dorcus-clerk",
    Comcen: "/dashboard/soi/n-a/comcen",
  };

  if (masterClerkRoutes[role]) {
    navigate(masterClerkRoutes[role]);
    return;
  }

  // All other SOI roles → BaseDashboard
  const soiRoutes = {
    Commandant: "/dashboard/soi/n-a/commandant",
    "Chief Instructor": "/dashboard/soi/n-a/chief-instructor",
    "Senior Instructor": "/dashboard/soi/n-a/senior-instructor",
    "Commanding Officer": "/dashboard/soi/n-a/commanding-officer",
    "Admin Officer": "/dashboard/soi/n-a/admin-officer",
    "Officer Commanding": "/dashboard/soi/n-a/officer-commanding",
    "Admin Wing": "/dashboard/soi/n-a/admin-wing",
  };

  const soiRoute = soiRoutes[role];
  if (soiRoute) {
    navigate(soiRoute);
    return;
  }

  // Keep the assigned appointment for newly-added SOI roles instead of
  // silently granting the Commandant dashboard.
  navigate(`/dashboard/soi/n-a/${formatPath(role)}`);
  return;
}

// ===== SIGNAL =====
if (
  unit === "SIGNAL" ||
  SIGNAL_ROUTE_HINTS.has(branchNorm) ||
  SIGNAL_ROUTE_HINTS.has(subBranchNorm) ||
  SIGNAL_ROUTE_HINTS.has(roleNorm)
) {
  const signalRoutes = {
    battalion: {
      concern: "/dashboard/signal-branch/battalion/concern",
      "archive-clerk": "/dashboard/signal-branch/battalion/archive-clerk",
      "dispatch-clerk": "/dashboard/signal-branch/battalion/dispatch-clerk",
      "dorcus-clerk": "/dashboard/signal-branch/battalion/dorcus-clerk",
      "chief-clerk": "/dashboard/chief-clerk/battalion",
      adjutant: "/dashboard/signal-branch/battalion/adjutant",
      "bn-2ic": "/dashboard/signal-branch/battalion/bn-2ic",
      co: "/dashboard/signal-branch/battalion/co",
    },
    "sos-branch": {
      clerk: "/dashboard/signal-branch/sos-branch/clerk",
      "chief-clerk": "/dashboard/chief-clerk/sos-branch",
      "admin-officer": "/dashboard/signal-branch/sos-branch/admin-officer",
      "si-comms": "/dashboard/signal-branch/sos-branch/si-comms",
      "so-comms": "/dashboard/signal-branch/sos-branch/si-comms",
      "si-it": "/dashboard/signal-branch/sos-branch/si-it",
      "so-it": "/dashboard/signal-branch/sos-branch/si-it",
      "si-tech": "/dashboard/signal-branch/sos-branch/si-tech",
      "so-tech": "/dashboard/signal-branch/sos-branch/si-tech",
      "chief-instructor": "/dashboard/signal-branch/sos-branch/chief-instructor",
      commandant: "/dashboard/signal-branch/sos-branch/commandant",
    },
    "it-squadron": {
      clerk: "/dashboard/signal-branch/it-squadron/clerk",
      oc: "/dashboard/signal-branch/it-squadron/oc",
    },
    "comms-squadron": {
      clerk: "/dashboard/signal-branch/comms-squadron/clerk",
      oc: "/dashboard/signal-branch/comms-squadron/oc",
    },
    kacs: {
      "registry-clerk": "/dashboard/signal-branch/kacs/registry-clerk",
      "dorcus-clerk": "/dashboard/signal-branch/kacs/dorcus-clerk",
      "training-clerk": "/dashboard/signal-branch/kacs/training-clerk",
      "chief-clerk": "/dashboard/chief-clerk/kacs",
      "master-clerk": "/dashboard/signal-branch/kacs/master-clerk",
      so2: "/dashboard/signal-branch/kacs/so2",
      commander: "/dashboard/signal-branch/kacs/commander",
    },
  };

  const branchPath = formatPath(branch);
  const subBranchPath = formatPath(subBranch);
  const rolePath = formatPath(role);

  let signalBranchKey = null;
  if (branchPath === "signal-branch" || branchPath === "kahawa-garrison-sig-bn") {
    signalBranchKey = subBranchPath;
  } else if (branchPath === "sos") {
    signalBranchKey = "sos-branch";
  } else if (branchPath === "it-sqdrn") {
    signalBranchKey = "it-squadron";
  } else if (branchPath === "comms-sqdrn") {
    signalBranchKey = "comms-squadron";
  } else if (branchPath === "battalion" || branchPath === "sos-branch" || branchPath === "kacs") {
    signalBranchKey = branchPath;
  } else if (subBranchPath === "sos") {
    signalBranchKey = "sos-branch";
  } else if (subBranchPath === "it-sqdrn") {
    signalBranchKey = "it-squadron";
  } else if (subBranchPath === "comms-sqdrn") {
    signalBranchKey = "comms-squadron";
  } else if (subBranchPath === "battalion" || subBranchPath === "sos-branch" || subBranchPath === "kacs") {
    signalBranchKey = subBranchPath;
  }

  if (signalBranchKey && signalRoutes[signalBranchKey]?.[rolePath]) {
    navigate(signalRoutes[signalBranchKey][rolePath]);
    return;
  }

  // fallback
  navigate("/dashboard/signal-branch/battalion/dispatch-clerk");
  return;
}

// ===== INFANTRY / 2ND BRIGADE =====
if (unit === "INFANTRY" || INFANTRY_ROUTE_HINTS.has(branchNorm) || INFANTRY_ROUTE_HINTS.has(subBranchNorm)) {
  const infantryRoutes = {
    "2-bde-hq": {
      "2-bde-hq": {
        "ber-comd": "/dashboard/2nd-brigade/2-bde-hq/ber-comd",
        "ber-2ic": "/dashboard/2nd-brigade/2-bde-hq/ber-2ic",
        "ber-opso": "/dashboard/2nd-brigade/2-bde-hq/ber-opso",
        "ber-adjutant": "/dashboard/2nd-brigade/2-bde-hq/ber-adjutant",
        "chief-clerk": "/dashboard/2nd-brigade/2-bde-hq/chief-clerk",
      },
    },
    "3kr": {
      battalion: {
        "typing-clerk": "/dashboard/2nd-brigade/3kr/battalion/typing-clerk",
        "registry-clerk": "/dashboard/2nd-brigade/3kr/battalion/registry-clerk",
        "dorcus-clerk": "/dashboard/2nd-brigade/3kr/battalion/dorcus-clerk",
        "chief-clerk": "/dashboard/2nd-brigade/3kr/battalion/chief-clerk",
        adjutant: "/dashboard/2nd-brigade/3kr/battalion/adjutant",
        co: "/dashboard/2nd-brigade/3kr/battalion/co",
      },
      "a-company": {
        "typing-clerk": "/dashboard/2nd-brigade/3kr/a-company/typing-clerk",
        "chief-clerk": "/dashboard/2nd-brigade/3kr/a-company/chief-clerk",
        "admin-officer": "/dashboard/2nd-brigade/3kr/a-company/admin-officer",
        oc: "/dashboard/2nd-brigade/3kr/a-company/oc",
      },
      "b-company": {
        "typing-clerk": "/dashboard/2nd-brigade/3kr/b-company/typing-clerk",
        "chief-clerk": "/dashboard/2nd-brigade/3kr/b-company/chief-clerk",
        "admin-officer": "/dashboard/2nd-brigade/3kr/b-company/admin-officer",
        oc: "/dashboard/2nd-brigade/3kr/b-company/oc",
      },
      "c-company": {
        "typing-clerk": "/dashboard/2nd-brigade/3kr/c-company/typing-clerk",
        "chief-clerk": "/dashboard/2nd-brigade/3kr/c-company/chief-clerk",
        "admin-officer": "/dashboard/2nd-brigade/3kr/c-company/admin-officer",
        oc: "/dashboard/2nd-brigade/3kr/c-company/oc",
      },
      "d-company": {
        "typing-clerk": "/dashboard/2nd-brigade/3kr/d-company/typing-clerk",
        "chief-clerk": "/dashboard/2nd-brigade/3kr/d-company/chief-clerk",
        "admin-officer": "/dashboard/2nd-brigade/3kr/d-company/admin-officer",
        oc: "/dashboard/2nd-brigade/3kr/d-company/oc",
      },
      "e-company": {
        "typing-clerk": "/dashboard/2nd-brigade/3kr/e-company/typing-clerk",
        "chief-clerk": "/dashboard/2nd-brigade/3kr/e-company/chief-clerk",
        "admin-officer": "/dashboard/2nd-brigade/3kr/e-company/admin-officer",
        oc: "/dashboard/2nd-brigade/3kr/e-company/oc",
      },
    },
    "5kr": {
      battalion: {
        "typing-clerk": "/dashboard/2nd-brigade/5kr/battalion/typing-clerk",
        "registry-clerk": "/dashboard/2nd-brigade/5kr/battalion/registry-clerk",
        "dorcus-clerk": "/dashboard/2nd-brigade/5kr/battalion/dorcus-clerk",
        "chief-clerk": "/dashboard/2nd-brigade/5kr/battalion/chief-clerk",
        adjutant: "/dashboard/2nd-brigade/5kr/battalion/adjutant",
        co: "/dashboard/2nd-brigade/5kr/battalion/co",
      },
      "a-company": {
        "typing-clerk": "/dashboard/2nd-brigade/5kr/a-company/typing-clerk",
        "chief-clerk": "/dashboard/2nd-brigade/5kr/a-company/chief-clerk",
        "admin-officer": "/dashboard/2nd-brigade/5kr/a-company/admin-officer",
        oc: "/dashboard/2nd-brigade/5kr/a-company/oc",
      },
      "b-company": {
        "typing-clerk": "/dashboard/2nd-brigade/5kr/b-company/typing-clerk",
        "chief-clerk": "/dashboard/2nd-brigade/5kr/b-company/chief-clerk",
        "admin-officer": "/dashboard/2nd-brigade/5kr/b-company/admin-officer",
        oc: "/dashboard/2nd-brigade/5kr/b-company/oc",
      },
      "c-company": {
        "typing-clerk": "/dashboard/2nd-brigade/5kr/c-company/typing-clerk",
        "chief-clerk": "/dashboard/2nd-brigade/5kr/c-company/chief-clerk",
        "admin-officer": "/dashboard/2nd-brigade/5kr/c-company/admin-officer",
        oc: "/dashboard/2nd-brigade/5kr/c-company/oc",
      },
      "d-company": {
        "typing-clerk": "/dashboard/2nd-brigade/5kr/d-company/typing-clerk",
        "chief-clerk": "/dashboard/2nd-brigade/5kr/d-company/chief-clerk",
        "admin-officer": "/dashboard/2nd-brigade/5kr/d-company/admin-officer",
        oc: "/dashboard/2nd-brigade/5kr/d-company/oc",
      },
      "e-company": {
        "typing-clerk": "/dashboard/2nd-brigade/5kr/e-company/typing-clerk",
        "chief-clerk": "/dashboard/2nd-brigade/5kr/e-company/chief-clerk",
        "admin-officer": "/dashboard/2nd-brigade/5kr/e-company/admin-officer",
        oc: "/dashboard/2nd-brigade/5kr/e-company/oc",
      },
    },
    "9kr": {
      battalion: {
        "typing-clerk": "/dashboard/2nd-brigade/9kr/battalion/typing-clerk",
        "registry-clerk": "/dashboard/2nd-brigade/9kr/battalion/registry-clerk",
        "dorcus-clerk": "/dashboard/2nd-brigade/9kr/battalion/dorcus-clerk",
        "chief-clerk": "/dashboard/2nd-brigade/9kr/battalion/chief-clerk",
        adjutant: "/dashboard/2nd-brigade/9kr/battalion/adjutant",
        co: "/dashboard/2nd-brigade/9kr/battalion/co",
      },
      "a-company": {
        "typing-clerk": "/dashboard/2nd-brigade/9kr/a-company/typing-clerk",
        "chief-clerk": "/dashboard/2nd-brigade/9kr/a-company/chief-clerk",
        "admin-officer": "/dashboard/2nd-brigade/9kr/a-company/admin-officer",
        oc: "/dashboard/2nd-brigade/9kr/a-company/oc",
      },
      "b-company": {
        "typing-clerk": "/dashboard/2nd-brigade/9kr/b-company/typing-clerk",
        "chief-clerk": "/dashboard/2nd-brigade/9kr/b-company/chief-clerk",
        "admin-officer": "/dashboard/2nd-brigade/9kr/b-company/admin-officer",
        oc: "/dashboard/2nd-brigade/9kr/b-company/oc",
      },
      "c-company": {
        "typing-clerk": "/dashboard/2nd-brigade/9kr/c-company/typing-clerk",
        "chief-clerk": "/dashboard/2nd-brigade/9kr/c-company/chief-clerk",
        "admin-officer": "/dashboard/2nd-brigade/9kr/c-company/admin-officer",
        oc: "/dashboard/2nd-brigade/9kr/c-company/oc",
      },
      "d-company": {
        "typing-clerk": "/dashboard/2nd-brigade/9kr/d-company/typing-clerk",
        "chief-clerk": "/dashboard/2nd-brigade/9kr/d-company/chief-clerk",
        "admin-officer": "/dashboard/2nd-brigade/9kr/d-company/admin-officer",
        oc: "/dashboard/2nd-brigade/9kr/d-company/oc",
      },
      "e-company": {
        "typing-clerk": "/dashboard/2nd-brigade/9kr/e-company/typing-clerk",
        "chief-clerk": "/dashboard/2nd-brigade/9kr/e-company/chief-clerk",
        "admin-officer": "/dashboard/2nd-brigade/9kr/e-company/admin-officer",
        oc: "/dashboard/2nd-brigade/9kr/e-company/oc",
      },
    },
    "21-tpt-coy": {
      "typing-clerk": "/dashboard/2nd-brigade/21-tpt-coy/typing-clerk",
      "chief-clerk": "/dashboard/2nd-brigade/21-tpt-coy/chief-clerk",
      "admin-officer": "/dashboard/2nd-brigade/21-tpt-coy/admin-officer",
      oc: "/dashboard/2nd-brigade/21-tpt-coy/oc",
    },
    "22-fld-wksp": {
      "typing-clerk": "/dashboard/2nd-brigade/22-fld-wksp/typing-clerk",
      "chief-clerk": "/dashboard/2nd-brigade/22-fld-wksp/chief-clerk",
      "admin-officer": "/dashboard/2nd-brigade/22-fld-wksp/admin-officer",
      oc: "/dashboard/2nd-brigade/22-fld-wksp/oc",
    },
    "23-occ": {
      "typing-clerk": "/dashboard/2nd-brigade/23-occ/typing-clerk",
      "chief-clerk": "/dashboard/2nd-brigade/23-occ/chief-clerk",
      "admin-officer": "/dashboard/2nd-brigade/23-occ/admin-officer",
      oc: "/dashboard/2nd-brigade/23-occ/oc",
    },
  };

  const branchPath = formatPath(branch);
  const subBranchPath = formatPath(subBranch);
  const rolePath = formatPath(role);

  if (infantryRoutes[branchPath]?.[subBranchPath]?.[rolePath]) {
    navigate(infantryRoutes[branchPath][subBranchPath][rolePath]);
    return;
  }

  navigate("/dashboard/2nd-brigade/3kr/battalion/typing-clerk");
  return;
}

      // ===== ASOB (MAIN HQ) =====
      // ✅ These now match your App.js routes under /dashboard/main-hq/operations/...
      if (
        unit === "ASOB" ||
        branchNorm === "HQ ASOB" ||
        ASOB_ROUTE_HINTS.has(branchNorm) ||
        ASOB_ROUTE_HINTS.has(subBranchNorm) ||
        ASOB_ROUTE_HINTS.has(roleNorm)
      ) {
        const asobRoutes = {
          "main-hq": {
            operations: {
              "comd-asob": "/dashboard/main-hq/operations/comd",
              "deputy-comd": "/dashboard/main-hq/operations/deputy-comd",
              "so2-ops-&-trg": "/dashboard/main-hq/operations/so2-ops-trg",
              "so3-ops-&-trg": "/dashboard/main-hq/operations/so3-ops-trg",

              // ✅ Your App.js now supports BOTH:
              // /so2-pers-log and /so2-pers-&-log
              "so2-pers-&-log": "/dashboard/main-hq/operations/so2-pers-&-log",
              "so3-pers-&-log": "/dashboard/main-hq/operations/so3-pers-log",

              "so2-cbt-int": "/dashboard/main-hq/operations/so2-cbt-int",
              mcc: "/dashboard/main-hq/operations/mcc",
            },

            // ✅ Your App.js has /dashboard/main-hq/operations/clerk
            // and you can add alias /dashboard/main-hq/clerks/clerk if you want.
            clerks: { clerk: "/dashboard/main-hq/operations/clerk" },
          },

          "20-para-bn": {
            command: {
              "co-20": "/dashboard/hq-20-para-bn/command/co",
              "bn-2ic": "/dashboard/hq-20-para-bn/command/bn-2ic",
              opso: "/dashboard/hq-20-para-bn/command/opso",
              adjutant: "/dashboard/hq-20-para-bn/command/adjutant",
              clerk: "/dashboard/hq-20-para-bn/command/clerk",
            },
          },

          "60-asb": {
            command: {
              "co-60": "/dashboard/hq-60-asb/command/co",
              "bn-2ic": "/dashboard/hq-60-asb/command/bn-2ic",
              opso: "/dashboard/hq-60-asb/command/opso",
              adjutant: "/dashboard/hq-60-asb/command/adjutant",
              clerk: "/dashboard/hq-60-asb/command/clerk",
            },
          },

          "40-ranger": {
            command: {
              "co-40": "/dashboard/hq-40-ranger/command/co",
              "bn-2ic": "/dashboard/hq-40-ranger/command/bn-2ic",
              opso: "/dashboard/hq-40-ranger/command/opso",
              adjutant: "/dashboard/hq-40-ranger/command/adjutant",
              clerk: "/dashboard/hq-40-ranger/command/clerk",
            },
          },

          sqts: {
            command: {
              "admin-officer": "/dashboard/hq-sqts/admin-officer",
              adjutant: "/dashboard/hq-sqts/adjutant",
            },
            clerks: { clerk: "/dashboard/hq-sqts/clerk" },
          },

          cau: {
            command: {
              oc: "/dashboard/cau/oc",
              bso: "/dashboard/cau/bso",
            },
            clerks: { clerk: "/dashboard/cau/clerk" },
          },
        };

        const branchKey = formatPath(branch);
        const subBranchKey = formatPath(subBranch) || "command";
        const roleKey = formatPath(role);

        const branchObj = asobRoutes[branchKey];
        if (branchObj) {
          const subBranchObj =
            branchObj[subBranchKey] || branchObj["clerks"] || branchObj["command"];

          if (subBranchObj && subBranchObj[roleKey]) {
            navigate(subBranchObj[roleKey]);
            return;
          } else if (subBranchObj) {
            navigate(Object.values(subBranchObj)[0]);
            return;
      }
    }

        navigate("/dashboard/hq-asob/main-hq/clerk");
        return;
      }

      // ===== ARMY HQs (unchanged) =====
      const roleUpper = (role || "").toUpperCase();
      const branchPath = formatPath(branch);
      const subBranchPath = formatPath(subBranch);
      const rolePath = formatPath(role);

      setTimeout(() => {
        if (["MA", "DAC", "COMD"].includes(roleUpper)) {
          navigate(`/dashboard/${rolePath}`);
        } else if (roleUpper.includes("CHIEF OF BRANCH")) {
          navigate(`/dashboard/${branchPath}/n-a/${rolePath}`);
        } else if (roleUpper === "CHIEF CLERK") {
          navigate(`/dashboard/chief-clerk/${subBranchPath}`);
        } else if (roleUpper === "CLERK") {
          navigate(`/dashboard/${branchPath}/${subBranchPath}/clerk`);
        } else {
          navigate(`/dashboard/${branchPath}/${subBranchPath}/${rolePath}`);
        }
      }, 600);
    } catch (err) {
      console.error("Login error:", err);
      const msg =
        err.name === "AbortError"
          ? "Login request timed out. Please verify backend and database are running."
          : "An error occurred. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      if (timeout) clearTimeout(timeout);
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container" style={loginStyle}>
      <div className="login-shell">
        <div className="login-box">
        <div className="login-brand-lockup">
          <div className="login-emblem" aria-label="Kenya Army crest" role="img" />
          <span className="login-kicker">Headquarters Kenya Army</span>
          <h1 className="login-brand-title">WAVEY</h1>
          <p className="login-brand-copy">Secure document workflow</p>
        </div>
        <h2 className="login-title">{isAdminMode ? "Admin Login" : "User Login"}</h2>
        <p className="login-subtitle">
          {isAdminMode
            ? "Use your admin credentials to continue to the control dashboard."
            : "Sign in to continue to the digital workflow platform."}
        </p>

        {error && <div className="error-message">{error}</div>}

        {twoFactor ? (
          <form onSubmit={handleTwoFactorVerification}>
            {twoFactor.enrollmentRequired && (
              <div className="form-group" style={{ textAlign: "center" }}>
                <p>Scan this QR code with Google Authenticator, then enter the six-digit code.</p>
                <img src={twoFactor.qrCode} alt="Google Authenticator setup QR code" width="220" height="220" />
                <p style={{ overflowWrap: "anywhere" }}><strong>Manual key:</strong> {twoFactor.manualKey}</p>
              </div>
            )}
            {!twoFactor.enrollmentRequired && (
              <p>Enter the six-digit code from Google Authenticator.</p>
            )}
            <div className="form-group">
              <label htmlFor="authenticatorCode" className="form-label">Authenticator code</label>
              <input
                id="authenticatorCode"
                className="form-input"
                value={authenticatorCode}
                onChange={(e) => setAuthenticatorCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                placeholder="000000"
                required
                autoFocus
              />
            </div>
            <button type="submit" className="login-button" disabled={isLoading || authenticatorCode.length !== 6}>
              {isLoading ? "Verifying..." : "Verify and continue"}
            </button>
            <button type="button" className="login-button" style={{ marginTop: 10 }} onClick={() => setTwoFactor(null)} disabled={isLoading}>
              Back to login
            </button>
          </form>
        ) : lostAuthenticator ? (
          <form onSubmit={requestAuthenticatorReset}>
            <p>Your request must be verified by your unit CompTech administrator. Access will not be restored automatically.</p>
            <div className="form-group">
              <label className="form-label" htmlFor="recoveryServiceNumber">Service number</label>
              <input id="recoveryServiceNumber" className="form-input" value={serviceNumber} onChange={(e) => setServiceNumber(e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="recoveryChannel">Verification method</label>
              <select id="recoveryChannel" className="form-input" value={recoveryChannel} onChange={(e) => setRecoveryChannel(e.target.value)}>
                <option value="EMAIL">Registered email</option>
                <option value="SMS">Registered phone number</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="recoveryContact">{recoveryChannel === "EMAIL" ? "Registered email" : "Registered phone number"}</label>
              <input id="recoveryContact" className="form-input" type={recoveryChannel === "EMAIL" ? "email" : "tel"} value={recoveryContact} onChange={(e) => setRecoveryContact(e.target.value)} required />
            </div>
            <button type="submit" className="login-button" disabled={isLoading}>{isLoading ? "Submitting..." : "Request Authenticator reset"}</button>
            <button type="button" className="login-button" style={{ marginTop: 10 }} onClick={() => setLostAuthenticator(false)}>Back to login</button>
          </form>
        ) : (
        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label htmlFor="serviceNumber" className="form-label">
              {isAdminMode ? "Username" : "Service Number"}
            </label>
            <div className="form-input-shell">
              <UserRound size={18} aria-hidden="true" />
              <input
                type="text"
                id="serviceNumber"
                value={serviceNumber}
                onChange={(e) => setServiceNumber(e.target.value)}
                className="form-input"
                placeholder={isAdminMode ? "Enter username" : "Enter service number"}
                autoComplete="username"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <div className="form-input-shell">
              <LockKeyhole size={18} aria-hidden="true" />
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input"
                placeholder="Enter password"
                autoComplete="current-password"
                required
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="password-toggle" aria-label={showPassword ? "Hide password" : "Show password"}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="login-inline-action">
            <div className="login-link-row">
              <button type="button"
                className="login-link"
                onClick={() => navigate("/forgot-password")}
              >
                <KeyRound size={14} /> Forgot password?
              </button>
              {!isAdminMode && (
                <button type="button" className="login-link" onClick={() => setLostAuthenticator(true)}>
                  Lost access to Authenticator?
                </button>
              )}
              <button type="button"
                className="login-link login-link-admin"
                onClick={() => navigate(isAdminMode ? "/login" : "/admin")}
              >
                <ShieldCheck size={14} /> {isAdminMode ? "User access" : "Admin access"}
              </button>
            </div>
          </div>

          <button type="submit" className="login-button" disabled={isLoading}>
            <span>{isLoading ? "Signing in..." : "Sign in"}</span>
            {!isLoading && <ArrowRight size={18} aria-hidden="true" />}
          </button>
          <div className="login-security-note"><ShieldCheck size={15} /><span>Encrypted access · Authorized personnel only</span></div>
        </form>
        )}
      </div>
      </div>
    </div>
  );
};

export default Login;

